import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    FormControl,
    FormRecord,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {
    defaultIfEmpty,
    firstValueFrom,
    from,
    isObservable,
    Observable,
    of,
} from 'rxjs';

export interface InputFormDialogFieldConfig {
    controlName: string;
    labelTranslationKey: string;
    validators: ValidatorFn[];
    infoTranslationKey?: string;
    initialValue?: string;
    maxLength?: number;
    type?: InputFormDialogFieldType;
    options?: InputFormDialogSelectOption[];
}

export enum InputFormDialogFieldType {
    TEXT = 'text',
    SELECT = 'select',
}

export interface InputFormDialogSelectOption {
    name: string;
    value: string;
}

export type InputFormDialogValues = Record<string, string>;

export type InputFormDialogSubmitActionResult =
    | Observable<unknown>
    | Promise<unknown>
    | void;

export type InputFormDialogSubmitAction = (
    values: InputFormDialogValues
) => InputFormDialogSubmitActionResult;

export type InputFormDialogErrorTranslationKeyResolver = (
    error: unknown
) => string;

export interface InputFormDialogData {
    headerTranslationKey: string;
    headerInterpolateParameters?: { [key: string]: string | number };
    submitButtonTranslationKey: string;
    cancelButtonTranslationKey?: string;
    fields: InputFormDialogFieldConfig[];
    submitAction: InputFormDialogSubmitAction;
    resolveErrorTranslationKey?: InputFormDialogErrorTranslationKeyResolver;
    defaultErrorTranslationKey?: string;
    maxLength?: number;
    inputErrorTranslationKeyPrefix?: string;
}

@Component({
    selector: 'app-input-form-dialog',
    templateUrl: './input-form-dialog.component.html',
    standalone: false,
})
export class InputFormDialogComponent {
    readonly form = new FormRecord<FormControl<string>>({});
    submitErrorTranslationKey: string | null = null;
    isSubmitting = false;

    constructor(
        public dialogRef: MatDialogRef<InputFormDialogComponent, unknown>,
        @Inject(MAT_DIALOG_DATA)
        public injectedData: InputFormDialogData
    ) {
        this.dialogRef.addPanelClass('tryb-input-form-dialog');
        this.initializeForm();
    }

    onCancel(): void {
        if (this.isSubmitting) {
            return;
        }

        this.dialogRef.close();
    }

    async onSubmit(): Promise<void> {
        const formValues = this.getSanitizedFormValues();
        if (this.isSubmitting) {
            return;
        }

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.isSubmitting = true;
        this.dialogRef.disableClose = true;
        this.submitErrorTranslationKey = null;

        try {
            const actionResult = this.injectedData.submitAction(formValues);
            let result = await firstValueFrom(
                this.toObservable(actionResult).pipe(defaultIfEmpty(null))
            );
            if (result === null || result === undefined) {
                result = { hint: 'everything is fine' };
            }
            this.dialogRef.close(result);
        } catch (error: unknown) {
            this.dialogRef.disableClose = false;
            this.submitErrorTranslationKey =
                this.resolveErrorTranslationKey(error);
        } finally {
            this.isSubmitting = false;
        }
    }

    getControl(controlName: string): FormControl<string> {
        return this.form.controls[controlName];
    }

    isFieldRequired(field: InputFormDialogFieldConfig): boolean {
        return field.validators.includes(Validators.required);
    }

    isSelectField(field: InputFormDialogFieldConfig): boolean {
        return field.type === InputFormDialogFieldType.SELECT;
    }
    isTextField(field: InputFormDialogFieldConfig): boolean {
        return (
            field.type === InputFormDialogFieldType.TEXT ||
            field.type === undefined
        );
    }

    private initializeForm(): void {
        for (const field of this.injectedData.fields) {
            this.form.addControl(
                field.controlName,
                new FormControl(this.getInitialValue(field), {
                    nonNullable: true,
                    validators: field.validators,
                })
            );
        }
    }

    private getSanitizedFormValues(): InputFormDialogValues {
        const values: InputFormDialogValues = {};

        for (const field of this.injectedData.fields) {
            const sanitizedValue = this.sanitizeFieldValue(
                field,
                this.getControl(field.controlName).value
            );
            values[field.controlName] = sanitizedValue;
            this.getControl(field.controlName).setValue(sanitizedValue);
        }

        return values;
    }

    private getInitialValue(field: InputFormDialogFieldConfig): string {
        if (field.initialValue !== undefined) {
            return field.initialValue;
        }

        if (this.isSelectField(field)) {
            return field.options?.[0]?.value ?? '';
        }

        return '';
    }

    private sanitizeFieldValue(
        field: InputFormDialogFieldConfig,
        rawValue: string
    ): string {
        if (this.isSelectField(field)) {
            return rawValue;
        }

        return rawValue.trim();
    }

    private toObservable(
        result: InputFormDialogSubmitActionResult
    ): Observable<unknown> {
        if (isObservable(result)) {
            return result;
        }

        if (result instanceof Promise) {
            return from(result);
        }

        return of(result);
    }

    private resolveErrorTranslationKey(error: unknown): string {
        if (this.injectedData.resolveErrorTranslationKey) {
            return this.injectedData.resolveErrorTranslationKey(error);
        }

        return this.injectedData.defaultErrorTranslationKey ?? '';
    }

    onFieldLeave(controlName: string): void {
        this.getControl(controlName).setValue(
            this.getControl(controlName).value.trim()
        );
    }
}
